import { useEffect, useRef, useState } from 'react';
import { NavigateOptions, useSearchParams } from 'react-router';

import { TData } from '@/shared/components/ui/data-table/context/data-table-context.types';
import { useFilterDefaults } from '@/shared/components/ui/data-table/hooks/use-filter-params/filter-defaults-context';
import { TUseFilterParams } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params.types';
import { normalizeSortedParam } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params.utils';
import { useFilterScope } from '@/shared/features/filter-preferences/context/filter-scope-context';

export const useHasFilters = () => {
  const [searchParams] = useSearchParams();

  const hasSearchParams = () => {
    const params = Object.fromEntries([...searchParams]);
    const filteredParams = Object.keys(params).filter(
      (key) =>
        key !== 'page' &&
        key !== 'pageSize' &&
        key !== 'sortBy' &&
        !key.toLowerCase().endsWith('view'),
    );
    return filteredParams.length > 0;
  };

  return { hasSearchParams: hasSearchParams() };
};

const EMPTY_PARAM_DATA = {
  isLoading: false,
  isFetching: false,
  items: [],
  pages: 0,
  itemCount: 0,
} as const;

export const useRequiredFilterParam = <T>(
  queryParam: string,
): { paramData: TData<T> | undefined } => {
  const [searchParams] = useSearchParams();
  const scope = useFilterScope();
  const hasParam = !!searchParams.get(queryParam);
  // An explicitly-cleared default ('') seeds nothing, so settle once the scope is
  // ready — otherwise the required list hangs on the loading overlay forever.
  const clearedDefault =
    scope.isReady && scope.resolveDefault(queryParam) === '';
  const [settled, setSettled] = useState(hasParam);

  useEffect(() => {
    if (hasParam || clearedDefault) setSettled(true);
  }, [hasParam, clearedDefault]);

  if (!queryParam || (settled && (hasParam || clearedDefault))) {
    return { paramData: undefined };
  }

  return {
    paramData: { ...EMPTY_PARAM_DATA, isLoading: !settled, items: [] as T[] },
  };
};

export const useFilterParams = (options: TUseFilterParams) => {
  const { queryParam, resetParamsOnChange = ['page'] } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const filterDefaults = useFilterDefaults();
  const scope = useFilterScope();

  const processValue = (values: string | string[]) => {
    if (Array.isArray(values)) {
      return values.join(',');
    }
    return values;
  };

  const codeDefault = options.defaultValue
    ? processValue(options.defaultValue)
    : undefined;
  // '' is an explicitly cleared default: it stops resolution but seeds nothing.
  const resolvedDefault = queryParam
    ? scope.resolveDefault(queryParam, codeDefault)
    : undefined;

  const seedDefault = (value: string) => {
    if (!queryParam || searchParams.has(queryParam) || !value) {
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set(queryParam, value);
    setSearchParams(newParams, { replace: true });
  };

  const removeParameter = () => {
    if (!queryParam) {
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    if (!newParams.has(queryParam)) {
      return;
    }
    newParams.delete(queryParam);
    setSearchParams(newParams);
  };

  const setSearchParam = (
    values: string | string[],
    navigateOptions?: NavigateOptions,
  ) => {
    if (!queryParam) {
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    const value = processValue(values);

    if (!value) {
      removeParameter();
      return;
    }

    // Reset specified parameters if they exist (to be used to reset page number when switching views)
    resetParamsOnChange?.forEach((param) => {
      if (newParams.has(param)) {
        newParams.delete(param);
      }
    });

    newParams.set(queryParam, value);

    setSearchParams(newParams, navigateOptions);
  };

  const seededRef = useRef(false);

  useEffect(() => {
    if (!queryParam || !scope.isReady) {
      return;
    }

    if (resolvedDefault) {
      filterDefaults?.registerDefault(queryParam, resolvedDefault);
    }

    // The scope owns seeding stored defaults, and after a restore it has already
    // decided every param -- so a filter only ever seeds its own code default.
    if (!seededRef.current) {
      seededRef.current = true;
      if (
        resolvedDefault &&
        !scope.didRestore &&
        !scope.hasStoredDefault(queryParam)
      ) {
        seedDefault(resolvedDefault);
      }
    }

    return () => {
      filterDefaults?.unregisterDefault(queryParam);
    };
  }, [queryParam, scope.isReady, resolvedDefault]);

  const searchParam = queryParam ? (searchParams.get(queryParam) ?? '') : '';

  // Central check so every filter — including the direct-SelectFilter group/tag/
  // user ones — gates save/reset consistently instead of each recomputing it.
  const isAtDefault =
    normalizeSortedParam(searchParam) ===
    normalizeSortedParam(resolvedDefault ?? '');

  return {
    searchParam,
    setSearchParam,
    removeParameter,
    resolvedDefault,
    isAtDefault,
  };
};
